// components/footfall/planSlashCommands.ts
// The "/" menu of a Plan conversation: Plan's own shortcuts, then the Plan agent's actions, and nothing of Source's.
// A shortcut is a NAME the Plan agent interprets (its knowledge base says what each one asks for), sent as a normal
// turn, the same convention as Source's menu.
import type { SourceSlashCommand } from '@/components/source/sourceSlashCommands';

export const PLAN_SLASH_COMMANDS: SourceSlashCommand[] = [
  { name: 'scenarios', group: 'plan', hint: 'Propose 1 to 3 scenarios for the centre, each with its reasoning' },
  { name: 'restructure', group: 'plan', hint: 'Rethink the layout: cut big units, join small ones, where it pays' },
  { name: 'compare', group: 'plan', hint: 'Compare the scenarios and say which one to keep' },
  { name: 'verify-shortlist', group: 'plan', hint: 'Check the candidate brands on the web before placing them' },
];

const canonical = (name: string) => name.toLowerCase().replace(/-/g, '_');

/** The menu for a typed "/fragment": shortcuts first, then the Plan agent's actions. */
export const matchPlanSlashCommands = (text: string, agentActions: readonly SourceSlashCommand[] = []): SourceSlashCommand[] | null => {
  const match = /^\/([a-zA-Z_-]*)$/.exec(text.trimStart());
  if (!match) return null;
  const fragment = (match[1] ?? '').toLowerCase();
  const hits = [...PLAN_SLASH_COMMANDS, ...agentActions].filter(command => command.name.toLowerCase().startsWith(fragment));
  return hits.length > 0 ? hits : null;
};

/** Is this line one of Plan's shortcuts (sent to the agent as a turn) rather than an action typed by hand? */
export const isPlanSlashCommandText = (text: string, agentActionNames: readonly string[] = []): boolean => {
  const name = /^\/([a-zA-Z_-]+)(\s|$)/.exec(text.trim())?.[1]?.toLowerCase();
  if (!name || agentActionNames.some(action => canonical(action) === canonical(name))) return false;
  return PLAN_SLASH_COMMANDS.some(command => command.name === name);
};
